import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Check, FileText, Plus, Search, SquarePen, Trash2, Users, X } from 'lucide-react';

import { InstructorLoadingSkeleton } from '../components/layout/InstructorLoadingSkeleton';
import { InstructorToast } from '../components/layout/InstructorToast';
import { useInstructorToast } from '../components/layout/useInstructorToast';
import {
  instructorService,
  type CourseStudent,
  type CourseSubmission,
  type CreateCourseData,
  type Dimension,
  type InstructorCourse,
  type UpdateCourseData,
} from '../services/instructorService';

type CourseMode = 'view' | 'edit' | 'create';
type DimensionCategory =
  | 'Purposefulness'
  | 'Disciplinary'
  | 'Integration'
  | 'Critical Awareness';

interface CourseFormData {
  code: string;
  title: string;
  description: string;
  term: string;
  passcode: string;
  dimensionIds: number[];
}

const dimensionCategories: DimensionCategory[] = [
  'Purposefulness',
  'Disciplinary',
  'Integration',
  'Critical Awareness',
];

const categoryColors: Record<DimensionCategory, string> = {
  Purposefulness: '#003D7C',
  Disciplinary: '#0D7377',
  Integration: '#B8860B',
  'Critical Awareness': '#C8102E',
};

const createEmptyCourseForm = (dimensions: Dimension[]): CourseFormData => ({
  code: '',
  title: '',
  description: '',
  term: '',
  passcode: '',
  dimensionIds: dimensions.map((dimension) => dimension.id),
});

const courseToForm = (course: InstructorCourse): CourseFormData => ({
  code: course.code,
  title: course.title,
  description: course.description || '',
  term: course.term || '',
  passcode: course.passcode || '',
  dimensionIds: course.dimension_ids || [],
});

const submissionStatusClassByStatus: Record<CourseSubmission['status'], string> = {
  submitted: 'instructor-status-submitted',
  scoring: 'instructor-status-scoring',
  scored: 'instructor-status-scored',
  reviewed: 'instructor-status-reviewed',
};

const submissionStatusLabelByStatus: Record<CourseSubmission['status'], string> = {
  submitted: 'Handed In',
  scoring: 'Scoring',
  scored: 'Scored',
  reviewed: 'Reviewed',
};

const padDatePart = (value: number) => value.toString().padStart(2, '0');

const formatCourseDate = (dateString: string | null) => {
  if (!dateString) {
    return 'No submissions';
  }

  const date = new Date(dateString);

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const getAverageScore = (submission: CourseSubmission) => {
  const score = Number(submission.avg_score);

  return Number.isFinite(score) ? score : 0;
};

const getScoreLabel = (submission: CourseSubmission) => {
  if (submission.scores_count === 0) {
    return 'Not scored';
  }

  return `${Math.round(getAverageScore(submission) * submission.scores_count)}/${submission.scores_count * 3}`;
};

const doesSubmissionBelongToStudent = (
  submission: CourseSubmission,
  student: CourseStudent
): boolean => {
  if (submission.author_user_id === student.id) {
    return true;
  }

  // Group submissions are shared with every project member. Individual
  // submissions must only be visible to their author.
  return (
    submission.project_type === 'group' &&
    submission.participant_user_ids.includes(student.id)
  );
};

const getDimensionCategory = (
  dimension: Dimension,
  index: number
): DimensionCategory => {
  const label = `${dimension.label} ${dimension.short_label || ''}`.toLowerCase();

  if (label.includes('frame') || label.includes('stakeholder')) {
    return 'Purposefulness';
  }

  if (
    label.includes('range') ||
    label.includes('reason') ||
    label.includes('credib') ||
    label.includes('disciplin')
  ) {
    return 'Disciplinary';
  }

  if (label.includes('integ') || label.includes('number') || label.includes('depth')) {
    return 'Integration';
  }

  if (label.includes('social') || label.includes('limit') || label.includes('critical')) {
    return 'Critical Awareness';
  }

  if (index <= 1) {
    return 'Purposefulness';
  }

  if (index <= 4) {
    return 'Disciplinary';
  }

  if (index <= 6) {
    return 'Integration';
  }

  return 'Critical Awareness';
};

const InstructorCourses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<InstructorCourse[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [courseStudents, setCourseStudents] = useState<CourseStudent[]>([]);
  const [courseSubmissions, setCourseSubmissions] = useState<CourseSubmission[]>([]);
  const [selectedActivityStudentId, setSelectedActivityStudentId] = useState<string | null>(null);
  const [isCourseActivityModalOpen, setIsCourseActivityModalOpen] = useState(false);
  const [mode, setMode] = useState<CourseMode>('view');
  const [formData, setFormData] = useState<CourseFormData>(
    createEmptyCourseForm([])
  );
  const [loading, setLoading] = useState(true);
  const [courseActivityLoading, setCourseActivityLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [courseActivityError, setCourseActivityError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { showToast, toast } = useInstructorToast();

  const selectedCourse =
    courses.find((course) => course.id === selectedCourseId) ?? null;
  const isEditable = mode === 'edit' || mode === 'create';
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredCourses = courses.filter(
    (course) =>
      normalizedSearch.length === 0 ||
      course.code.toLowerCase().includes(normalizedSearch) ||
      course.title.toLowerCase().includes(normalizedSearch)
  );
  const dimensionGroups = dimensionCategories.map((category) => ({
    category,
    dimensions: dimensions.filter(
      (dimension, index) => getDimensionCategory(dimension, index) === category
    ),
  }));
  const detailTitle =
    mode === 'create' ? 'Create Course' : mode === 'edit' ? 'Edit Course' : 'Course Details';
  const detailSub =
    mode === 'create'
      ? 'Set up a new course with code, title, and an optional passcode.'
      : mode === 'edit'
        ? 'Update course information. Changes apply immediately on save.'
        : 'Basic information shown to enrolled students.';
  const selectedActivityStudent =
    courseStudents.find((student) => student.id === selectedActivityStudentId) ?? null;
  const selectedStudentSubmissions = selectedActivityStudent
    ? courseSubmissions.filter((submission) =>
        doesSubmissionBelongToStudent(submission, selectedActivityStudent)
      )
    : [];

  const loadData = async (preferredSelectedId?: string) => {
    try {
      setLoading(true);
      setError('');
      const [coursesData, dimensionsData] = await Promise.all([
        instructorService.getCourses(),
        instructorService.getDimensions(),
      ]);

      setCourses(coursesData);
      setDimensions(dimensionsData);
      setSelectedCourseId((current) => {
        if (
          preferredSelectedId &&
          coursesData.some((course) => course.id === preferredSelectedId)
        ) {
          return preferredSelectedId;
        }

        if (current && coursesData.some((course) => course.id === current)) {
          return current;
        }

        return coursesData[0]?.id ?? null;
      });

      if (coursesData.length === 0) {
        setFormData(createEmptyCourseForm(dimensionsData));
        setMode('create');
      }
    } catch (err) {
      console.error('Failed to load courses:', err);
      setError(err instanceof Error ? err.message : 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCourseActivity = async () => {
      setSelectedActivityStudentId(null);

      if (!selectedCourseId) {
        setCourseStudents([]);
        setCourseSubmissions([]);
        setCourseActivityError('');
        setCourseActivityLoading(false);
        return;
      }

      try {
        setCourseActivityLoading(true);
        setCourseActivityError('');
        const [studentsData, submissionsData] = await Promise.all([
          instructorService.getCourseStudents(selectedCourseId),
          instructorService.getCourseSubmissions(selectedCourseId),
        ]);

        if (!isMounted) {
          return;
        }

        setCourseStudents(studentsData);
        setCourseSubmissions(submissionsData);
      } catch (err) {
        console.error('Failed to load course activity:', err);

        if (isMounted) {
          setCourseStudents([]);
          setCourseSubmissions([]);
          setCourseActivityError(
            err instanceof Error ? err.message : 'Failed to load course activity'
          );
        }
      } finally {
        if (isMounted) {
          setCourseActivityLoading(false);
        }
      }
    };

    loadCourseActivity();

    return () => {
      isMounted = false;
    };
  }, [selectedCourseId]);

  useEffect(() => {
    if (mode === 'create') {
      return;
    }

    if (selectedCourse) {
      setFormData(courseToForm(selectedCourse));
    }
  }, [mode, selectedCourse]);

  const handleSelectCourse = (course: InstructorCourse) => {
    setSelectedCourseId(course.id);
    setIsCourseActivityModalOpen(false);
    setSelectedActivityStudentId(null);
    setMode('view');
    setSaveError('');
  };

  const handleCreateCourse = () => {
    setSelectedCourseId(null);
    setIsCourseActivityModalOpen(false);
    setSelectedActivityStudentId(null);
    setFormData(createEmptyCourseForm(dimensions));
    setMode('create');
    setSaveError('');
  };

  const handleEditCourse = () => {
    if (!selectedCourse) {
      return;
    }

    setFormData(courseToForm(selectedCourse));
    setMode('edit');
    setSaveError('');
  };

  const handleCancel = () => {
    setSaveError('');

    if (mode === 'create') {
      const firstCourse = courses[0] ?? null;
      setSelectedCourseId(firstCourse?.id ?? null);
      setMode(firstCourse ? 'view' : 'create');
      return;
    }

    if (selectedCourse) {
      setFormData(courseToForm(selectedCourse));
    }

    setMode('view');
  };

  const handleDimensionToggle = (dimensionId: number) => {
    if (!isEditable) {
      return;
    }

    setFormData((current) => {
      const dimensionIds = current.dimensionIds.includes(dimensionId)
        ? current.dimensionIds.filter((id) => id !== dimensionId)
        : [...current.dimensionIds, dimensionId].sort((first, second) => first - second);

      return { ...current, dimensionIds };
    });
  };

  const handleSelectAllDimensions = () => {
    setFormData((current) => ({
      ...current,
      dimensionIds: dimensions.map((dimension) => dimension.id),
    }));
  };

  const handleClearDimensions = () => {
    setFormData((current) => ({ ...current, dimensionIds: [] }));
  };

  const showSaveError = (message: string) => {
    setSaveError(message);
    showToast(message, 'error');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError('');

    const code = formData.code.trim();
    const title = formData.title.trim();

    if (!code || !title) {
      showSaveError('Course code and title are required.');
      return;
    }

    if (formData.dimensionIds.length === 0) {
      showSaveError('Please select at least one rubric dimension.');
      return;
    }

    try {
      setSaving(true);

      if (mode === 'create') {
        const createData: CreateCourseData = {
          code,
          title,
          description: formData.description.trim(),
          term: formData.term.trim(),
          passcode: formData.passcode.trim() || undefined,
          dimension_ids: formData.dimensionIds,
        };
        const createdCourse = await instructorService.createCourse(createData);
        await loadData(createdCourse.id);
        setMode('view');
        showToast(`Course ${createdCourse.code} created`);
        return;
      }

      if (!selectedCourse) {
        return;
      }

      const updateData: UpdateCourseData = {
        code,
        title,
        description: formData.description.trim(),
        term: formData.term.trim(),
        passcode: formData.passcode.trim() || undefined,
        dimension_ids: formData.dimensionIds,
      };
      const updatedCourse = await instructorService.updateCourse(
        selectedCourse.id,
        updateData
      );
      await loadData(updatedCourse.id);
      setMode('view');
      showToast(`Course ${updatedCourse.code} updated`);
    } catch (err) {
      console.error('Failed to save course:', err);
      showSaveError(err instanceof Error ? err.message : 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) {
      return;
    }

    const shouldDelete = window.confirm(
      `Delete ${selectedCourse.code}? This action cannot be undone.`
    );

    if (!shouldDelete) {
      return;
    }

    try {
      const courseCode = selectedCourse.code;
      await instructorService.deleteCourse(selectedCourse.id);
      await loadData();
      setIsCourseActivityModalOpen(false);
      setSelectedActivityStudentId(null);
      setMode('view');
      showToast(`Course ${courseCode} deleted`);
    } catch (err) {
      console.error('Failed to delete course:', err);
      showSaveError(err instanceof Error ? err.message : 'Failed to delete course');
    }
  };

  const handleOpenSubmission = (submissionId: string) => {
    navigate(`/instructor/submissions/${submissionId}`);
  };

  const handleOpenCourseActivity = () => {
    setSelectedActivityStudentId(null);
    setIsCourseActivityModalOpen(true);
  };

  if (loading) {
    return (
      <div className="instructor-page instructor-design-page instructor-page-fixed">
        <InstructorLoadingSkeleton message="Loading courses..." variant="courses" />
      </div>
    );
  }

  return (
    <div className="instructor-page instructor-design-page instructor-page-fixed">
      <div className="instructor-top-bar">
        <div>
          <h1 className="instructor-page-title">Course Management</h1>
          <div className="instructor-page-sub">Create and manage your courses</div>
        </div>
      </div>

      {error && <div className="instructor-error-state compact">{error}</div>}

      <div className="instructor-courses-grid-v2">
        <aside className="instructor-course-list-panel">
          <div className="instructor-list-header">
            <div className="instructor-list-title-row">
              <div className="instructor-list-title">
                <span>Your Courses</span>
                <span className="instructor-list-count">{courses.length}</span>
              </div>
              <button className="instructor-new-course-button" onClick={handleCreateCourse} type="button">
                <Plus strokeWidth={2.5} />
                New
              </button>
            </div>
            <div className="instructor-search-wrap design-search">
              <Search strokeWidth={2} />
              <input
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by code or title..."
                type="search"
                value={searchTerm}
              />
            </div>
          </div>

          <div className="instructor-course-list-scroll">
            {filteredCourses.length === 0 ? (
              <div className="instructor-empty-state">
                {searchTerm
                  ? 'No courses match your search.'
                  : 'Create your first course to get started.'}
              </div>
            ) : (
              filteredCourses.map((course) => (
                <button
                  className={
                    course.id === selectedCourseId
                      ? 'instructor-course-card active'
                      : 'instructor-course-card'
                  }
                  key={course.id}
                  onClick={() => handleSelectCourse(course)}
                  type="button"
                >
                  <div className="instructor-course-card-header">
                    <span className="instructor-course-code">{course.code}</span>
                    <span>{course.term || 'No term'}</span>
                  </div>
                  <div className="instructor-course-card-title">{course.title}</div>
                  <div className="instructor-course-card-meta">
                    <span><Users strokeWidth={2} /><strong>{course.enrolled_count}</strong> students</span>
                    <span><FileText strokeWidth={2} /><strong>{course.submission_count}</strong> submissions</span>
                    <span><Check strokeWidth={2} /><strong>{course.dimension_ids?.length || 0}</strong>/9 dims</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        <form className="instructor-course-combined-detail" onSubmit={handleSubmit}>
          <div className="instructor-course-combined-body">
            <section className="instructor-course-detail-panel">
              <div className="instructor-course-detail-header">
                <div>
                  <span className={`instructor-mode-tag ${mode}`}>
                    <span />
                    {mode === 'create' ? 'New' : mode === 'edit' ? 'Editing' : 'Viewing'}
                  </span>
                  <h2>{detailTitle}</h2>
                  <p>{detailSub}</p>
                </div>
                {mode !== 'create' && selectedCourse && (
                  <button
                    aria-label="Delete course"
                    className="instructor-delete-icon-button"
                    onClick={handleDeleteCourse}
                    type="button"
                  >
                    <Trash2 strokeWidth={2.2} />
                  </button>
                )}
              </div>

              <div className="instructor-course-detail-body">
                {mode === 'view' && !selectedCourse ? (
                  <div className="instructor-empty-state">Select or create a course.</div>
                ) : (
                  <>
                    <div className="instructor-course-form-grid">
                    <div className="instructor-form-row">
                      <label className="instructor-form-label" htmlFor="course-code">
                        Course Code <span className="required">*</span>
                      </label>
                      <input
                        className="instructor-form-input design-input"
                        id="course-code"
                        maxLength={20}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            code: event.target.value,
                          }))
                        }
                        placeholder="e.g. CC0002"
                        readOnly={!isEditable}
                        value={formData.code}
                      />
                    </div>

                    <div className="instructor-form-row">
                      <label className="instructor-form-label" htmlFor="course-term">
                        Term
                      </label>
                      <input
                        className="instructor-form-input design-input"
                        id="course-term"
                        maxLength={40}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            term: event.target.value,
                          }))
                        }
                        placeholder="e.g. Fall 2025"
                        readOnly={!isEditable}
                        value={formData.term}
                      />
                    </div>

                    <div className="instructor-form-row full">
                      <label className="instructor-form-label" htmlFor="course-title">
                        Course Title <span className="required">*</span>
                      </label>
                      <input
                        className="instructor-form-input design-input"
                        id="course-title"
                        maxLength={120}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            title: event.target.value,
                          }))
                        }
                        placeholder="e.g. Navigating the Digital World"
                        readOnly={!isEditable}
                        value={formData.title}
                      />
                    </div>

                    <div className="instructor-form-row full">
                      <label className="instructor-form-label" htmlFor="course-description">
                        Description
                      </label>
                      <textarea
                        className="instructor-form-textarea design-input"
                        id="course-description"
                        maxLength={1000}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            description: event.target.value,
                          }))
                        }
                        placeholder="Brief course description shown to enrolled students..."
                        readOnly={!isEditable}
                        value={formData.description}
                      />
                      <div className="instructor-form-hint align-right">
                        {formData.description.length}/1000
                      </div>
                    </div>

                    <div className="instructor-form-row full">
                      <label className="instructor-form-label" htmlFor="course-passcode">
                        Course Passcode <span className="optional">(optional)</span>
                      </label>
                      <input
                        className="instructor-form-input design-input"
                        id="course-passcode"
                        maxLength={40}
                        onChange={(event) =>
                          setFormData((current) => ({
                            ...current,
                            passcode: event.target.value,
                          }))
                        }
                        placeholder="e.g. CC0002"
                        readOnly={!isEditable}
                        value={formData.passcode}
                      />
                      <div className="instructor-form-hint">
                        Case-sensitive. Students must enter this passcode to enroll.
                      </div>
                    </div>
                    </div>

                    {mode !== 'create' && selectedCourse && (
                      <div className="instructor-course-activity-summary">
                        {courseActivityError && (
                          <div className="instructor-error-state compact">
                            {courseActivityError}
                          </div>
                        )}

                        <button
                          className="instructor-course-activity-summary-card"
                          onClick={handleOpenCourseActivity}
                          type="button"
                        >
                          <span><Users strokeWidth={2} /> Registered Students</span>
                          <strong>{courseActivityLoading ? '...' : courseStudents.length}</strong>
                          <small>Open roster and select a student.</small>
                        </button>

                        <button
                          className="instructor-course-activity-summary-card"
                          onClick={handleOpenCourseActivity}
                          type="button"
                        >
                          <span><FileText strokeWidth={2} /> Course Submissions</span>
                          <strong>{courseActivityLoading ? '...' : courseSubmissions.length}</strong>
                          <small>Submissions appear after choosing a student.</small>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            <section className="instructor-course-detail-panel">
              <div className="instructor-course-detail-header">
                <div>
                  <span className="instructor-mode-tag scoring">
                    <span />
                    Scoring
                  </span>
                  <h2>Rubric Dimensions</h2>
                  <p>
                    9 dimensions across 4 categories - choose what counts for this course.
                  </p>
                </div>
                <div className="instructor-rubric-header-actions">
                  <span>{formData.dimensionIds.length} of {dimensions.length}</span>
                  {isEditable && (
                    <div>
                      <button onClick={handleSelectAllDimensions} type="button">All</button>
                      <i />
                      <button onClick={handleClearDimensions} type="button">Clear</button>
                    </div>
                  )}
                </div>
              </div>

              <div className="instructor-course-detail-body">
                <div className={isEditable ? 'instructor-rubric-list' : 'instructor-rubric-list readonly'}>
                  {dimensionGroups.map(({ category, dimensions: categoryDimensions }) =>
                    categoryDimensions.length > 0 ? (
                      <div className="instructor-rubric-category" key={category}>
                        <div className="instructor-rubric-category-title">
                          <span style={{ backgroundColor: categoryColors[category] }} />
                          {category}
                        </div>
                        {categoryDimensions.map((dimension) => {
                          const checked = formData.dimensionIds.includes(dimension.id);
                          const dimColor = dimension.color_hex || categoryColors[category];

                          return (
                            <button
                              className={checked ? 'instructor-rubric-item checked' : 'instructor-rubric-item'}
                              disabled={!isEditable}
                              key={dimension.id}
                              onClick={() => handleDimensionToggle(dimension.id)}
                              style={{
                                borderColor: `${dimColor}66`,
                                borderLeftColor: dimColor,
                              }}
                              type="button"
                            >
                              <span className="instructor-rubric-check">
                                {checked && <Check strokeWidth={3} />}
                              </span>
                              <span className="instructor-rubric-text">
                                <strong>
                                  <span>{dimension.id}.</span> {dimension.label}
                                </strong>
                                <small>{dimension.short_label || dimension.description || 'Rubric dimension'}</small>
                              </span>
                              <span
                                className="instructor-rubric-dot"
                                style={{ backgroundColor: dimColor }}
                              />
                            </button>
                          );
                        })}
                      </div>
                    ) : null
                  )}
                </div>
                <div className="instructor-rubric-footer-hint">
                  {isEditable
                    ? '9 dimensions across 4 categories. Dimensions you disable here will not appear in feedback or analytics for this course.'
                    : `${formData.dimensionIds.length} of ${dimensions.length} dimensions enabled for this course. Click Edit Course to adjust.`}
                </div>
              </div>
            </section>
          </div>

          <div className={isEditable ? 'instructor-course-footer' : 'instructor-course-footer view-mode'}>
            {isEditable && (
              <div>
                <span className="instructor-footer-hint">
                  Course details and rubric dimensions are saved together.
                </span>
                {saveError && <div className="instructor-save-error">{saveError}</div>}
              </div>
            )}

            <div className="instructor-course-footer-actions">
              {isEditable ? (
                <>
                  <button
                    className="instructor-secondary-button"
                    onClick={handleCancel}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button className="instructor-button" disabled={saving} type="submit">
                    {saving ? 'Saving...' : mode === 'create' ? 'Create Course' : 'Update Course'}
                  </button>
                </>
              ) : (
                <button
                  className="instructor-button"
                  disabled={!selectedCourse}
                  onClick={handleEditCourse}
                  type="button"
                >
                  <SquarePen className="h-4 w-4" strokeWidth={2.4} />
                  Edit Course
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {isCourseActivityModalOpen && selectedCourse && (
        <div
          className="instructor-modal-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsCourseActivityModalOpen(false);
            }
          }}
        >
          <div
            aria-labelledby="course-activity-title"
            aria-modal="true"
            className="instructor-course-activity-modal"
            role="dialog"
          >
            <button
              aria-label="Close course activity"
              className="instructor-modal-close"
              onClick={() => setIsCourseActivityModalOpen(false)}
              type="button"
            >
              <X strokeWidth={2.5} />
            </button>

            <div className="instructor-course-activity-modal-header">
              <span className="instructor-mode-tag viewing">
                <span />
                Roster
              </span>
              <h2 id="course-activity-title">
                {selectedCourse.code} · {selectedCourse.title}
              </h2>
              <p>Select a registered student to view that student's submissions for this course.</p>
            </div>

            {courseActivityError ? (
              <div className="instructor-error-state compact">{courseActivityError}</div>
            ) : courseActivityLoading ? (
              <div className="instructor-loading-state compact">Loading course roster...</div>
            ) : (
              <div className="instructor-course-activity-modal-grid">
                <section className="instructor-course-activity-panel">
                  <div className="instructor-course-activity-panel-heading">
                    <span>Registered Students</span>
                    <strong>{courseStudents.length}</strong>
                  </div>

                  {courseStudents.length === 0 ? (
                    <div className="instructor-empty-state compact">
                      No registered students yet.
                    </div>
                  ) : (
                    <div className="instructor-course-activity-list">
                      {courseStudents.map((student) => (
                        <button
                          className={
                            student.id === selectedActivityStudentId
                              ? 'instructor-course-person-row active'
                              : 'instructor-course-person-row'
                          }
                          key={student.id}
                          onClick={() => setSelectedActivityStudentId(student.id)}
                          type="button"
                        >
                          <div className="instructor-course-row-main">
                            <strong>{student.display_name}</strong>
                            <span>{student.email}</span>
                            <small>
                              {student.student_id || 'No student ID'} · {student.department || 'No department'}
                            </small>
                          </div>
                          <div className="instructor-course-row-side">
                            <strong>{student.submission_count}</strong>
                            <span>submissions</span>
                            <small>Enrolled: {formatCourseDate(student.enrolled_at)}</small>
                            <small>Last: {formatCourseDate(student.last_submission_at)}</small>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                <section className="instructor-course-activity-panel">
                  <div className="instructor-course-activity-panel-heading">
                    <span>Student Submissions</span>
                    <strong>{selectedActivityStudent ? selectedStudentSubmissions.length : '-'}</strong>
                  </div>

                  {!selectedActivityStudent ? (
                    <div className="instructor-empty-state compact">
                      Select a student to view submissions for this course.
                    </div>
                  ) : selectedStudentSubmissions.length === 0 ? (
                    <div className="instructor-empty-state compact">
                      No submissions from {selectedActivityStudent.display_name} yet.
                    </div>
                  ) : (
                    <div className="instructor-course-activity-list">
                      {selectedStudentSubmissions.map((submission) => (
                        <button
                          className="instructor-course-submission-row"
                          key={submission.id}
                          onClick={() => handleOpenSubmission(submission.id)}
                          type="button"
                        >
                          <div className="instructor-course-row-main">
                            <strong>{submission.project_title}</strong>
                            <span>{submission.student_name} · {submission.name}</span>
                            <small>
                              {submission.project_type} · {formatCourseDate(submission.submitted_at)}
                            </small>
                          </div>
                          <div className="instructor-course-row-side">
                            <span
                              className={`instructor-status-badge ${submissionStatusClassByStatus[submission.status]}`}
                            >
                              <span className="instructor-status-dot" />
                              {submissionStatusLabelByStatus[submission.status]}
                            </span>
                            <strong>{getScoreLabel(submission)}</strong>
                            <small>Open details</small>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>
      )}

      <InstructorToast toast={toast} />
    </div>
  );
};

export { InstructorCourses };
